<script lang="ts">
import { type FunctionalComponent, h, useCssModule } from 'vue'

export const sizes = ['small', 'medium', 'large'] as const
export const variants = [
  'primary', // accent-fill
  'secondary', // accent-outline accent-label
  'base', // low-emphasis-outline
  'plain', // accent-label
  'conversion', // complementary-fill
  'caution', // error-fill
  'line', // LINE brand color
] as const
export type Size = ValueOf<typeof sizes>
export type Variant = ValueOf<typeof variants>

type Props = {
  size: Size
  variant: Variant
  disabled?: boolean
}

const detectClass = (size: Size, variant: Variant, disabled: boolean) => {
  const style = useCssModule()
  return [
    style.button,
    style[`button--${size}`],
    style[`button--${variant}`],
    {
      [style[`button--enabled`]]: !disabled,
      [style[`button--disabled`]]: disabled,
    },
  ]
}

const FlatButtonStyle: FunctionalComponent<Props> = (props, { slots, attrs }) =>
  slots.default?.().map(vnode =>
    h(vnode, {
      ...attrs,
      class: [
        attrs.class,
        ...detectClass(props.size, props.variant, !!props.disabled),
      ],
    })
  )

export default FlatButtonStyle
</script>

<style lang="scss" module>
.button {
  $root: &;

  border: 1px solid transparent;
  border-radius: $border-radius-v2-base;
  font-weight: 700;

  &--disabled {
    cursor: not-allowed;
  }

  &:hover {
    text-decoration: none;
  }

  &:focus-visible {
    // TODO: デザイン的に適切な色を確認する
    // 背景がfillのとき白ボーダーがつくので背景色によっては分かりづらい
    outline-offset: 4px;
  }

  &:focus:not(:focus-visible) {
    outline: none;
  }

  // Size
  &--small {
    font-size: 12px;
    padding: 4px 16px;
  }

  &--medium {
    font-size: 14px;
    padding: 8px 40px;
  }

  &--large {
    font-size: 16px;
    padding: 12px 52px;
  }

  // Variant
  &--primary {
    color: $color-text-and-object-high-emphasis-inverse;
    background-color: $color-surface-accent-primary;
    &#{$root}--enabled {
      &:hover {
        opacity: 0.9;
      }
    }
    &#{$root}--disabled {
      color: $color-text-and-object-disable;
      background-color: $color-surface-tertiary;
    }
  }

  &--secondary {
    color: $color-text-and-object-accent-primary;
    background-color: $color-surface-primary;
    border: 1px solid $color-border-accent-primary;

    &#{$root}--enabled {
      &:hover {
        opacity: 0.8;
      }
    }

    &#{$root}--disabled {
      color: $color-text-and-object-disable;
      background-color: $color-surface-primary;
      border: 1px solid $color-border-low-emphasis;
    }
  }

  &--base {
    color: $color-text-and-object-high-emphasis;
    background-color: $color-surface-primary;
    border: 1px solid $color-border-low-emphasis;

    &#{$root}--enabled {
      &:hover {
        background-color: $color-surface-secondary;
      }
    }

    &#{$root}--disabled {
      color: $color-text-and-object-disable;
      background-color: $color-surface-primary;
      border: 1px solid $color-border-low-emphasis;
    }
  }

  &--plain {
    color: $color-text-and-object-accent-primary;
    background-color: $color-surface-primary;

    &#{$root}--enabled {
      &:hover {
        background-color: $color-surface-accent-primary-light;
      }
    }

    &#{$root}--disabled {
      color: $color-text-and-object-disable;
      background-color: $color-surface-primary;
    }
  }

  &--conversion {
    color: $color-text-and-object-high-emphasis-inverse;
    background-color: $color-surface-accent-orange;
    &#{$root}--enabled {
      &:hover {
        opacity: 0.9;
      }
    }
    &#{$root}--disabled {
      color: $color-text-and-object-disable;
      background-color: $color-surface-tertiary;
    }
  }

  &--caution {
    color: $color-text-and-object-high-emphasis-inverse;
    background-color: $color-caution-accent;
    &#{$root}--enabled {
      &:hover {
        opacity: 0.9;
      }
    }
    &#{$root}--disabled {
      color: $color-text-and-object-disable;
      background-color: $color-surface-tertiary;
    }
  }

  &--line {
    color: $color-text-and-object-high-emphasis-inverse;
    background-color: $color-third-party-line;
    &#{$root}--enabled {
      &:hover {
        opacity: 0.9;
      }
    }
    &#{$root}--disabled {
      color: $color-text-and-object-disable;
      background-color: $color-surface-tertiary;
    }
  }
}
</style>
